import React, { useState } from "react";
import {
    Box,
    IconButton,
    Paper,
    Grid,
    useTheme,
    Modal,
    Backdrop,
    Fade
} from "@mui/material";
import { ChevronLeft, ChevronRight, Close, Fullscreen } from "@mui/icons-material";

const ImageGallery = ({ media, images, title }) => {
    // Support both new 'media' prop and old 'images' prop for backward compatibility
    const galleryItems = media && media.length > 0
        ? media
        : (images ? images.map(url => ({ type: "image", url })) : []);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [fullscreenOpen, setFullscreenOpen] = useState(false);
    const theme = useTheme();

    const nextItem = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % galleryItems.length);
    };

    const prevItem = () => {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + galleryItems.length) % galleryItems.length);
    };

    const selectItem = (index) => {
        setCurrentIndex(index);
    };

    const handleOpenFullscreen = () => {
        setFullscreenOpen(true);
    };

    const handleCloseFullscreen = () => {
        setFullscreenOpen(false);
    };

    if (!galleryItems || galleryItems.length === 0) {
        return (
            <Paper sx={{ p: 2, textAlign: "center", color: theme.palette.text.secondary }}>
        No media available.
            </Paper>
        );
    }

    const renderMedia = (item, style = {}) => {
        if (item.type === "video") {
            return (
                <video
                    src={item.url}
                    controls
                    style={{ width: "100%", height: "400px", objectFit: "cover", borderRadius: theme.shape.borderRadius, ...style }}
                />
            );
        }
        return (
            <img
                src={item.url}
                alt={title}
                style={{ width: "100%", height: "400px", objectFit: "cover", borderRadius: theme.shape.borderRadius, ...style }}
            />
        );
    };

    const renderThumbnail = (item, index) => (
        <Box
            key={index}
            onClick={() => selectItem(index)}
            sx={{
                cursor: "pointer",
                border: currentIndex === index ? `2px solid ${theme.palette.primary.main}` : "2px solid transparent",
                borderRadius: 1,
                overflow: "hidden",
                lineHeight: 0,
                position: "relative"
            }}
        >
            {item.type === "video" ? (
                <video src={item.url} style={{ width: "100%", height: "70px", objectFit: "cover" }} />
            ) : (
                <img src={item.url} alt={`thumbnail ${index + 1}`} style={{ width: "100%", height: "70px", objectFit: "cover" }} />
            )}
            {item.type === "video" && (
                <Box sx={{ position: "absolute", top: 4, left: 4, background: "rgba(0,0,0,0.5)", color: "white", px: 1, borderRadius: 1, fontSize: 12 }}>
          Video
                </Box>
            )}
        </Box>
    );

    return (
        <Paper elevation={3} sx={{ p: 2, position: "relative", overflow: "hidden" }}>
            <Box sx={{ position: "relative", mb: 1 }}>
                {renderMedia(galleryItems[currentIndex])}
                <IconButton
                    onClick={prevItem}
                    sx={{ position: "absolute", top: "50%", left: 8, transform: "translateY(-50%)", color: "white", backgroundColor: "rgba(0,0,0,0.5)", "&:hover": { backgroundColor: "rgba(0,0,0,0.7)"} }}
                >
                    <ChevronLeft />
                </IconButton>
                <IconButton
                    onClick={nextItem}
                    sx={{ position: "absolute", top: "50%", right: 8, transform: "translateY(-50%)", color: "white", backgroundColor: "rgba(0,0,0,0.5)", "&:hover": { backgroundColor: "rgba(0,0,0,0.7)"} }}
                >
                    <ChevronRight />
                </IconButton>
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 8,
                        left: 8,
                        backgroundColor: "rgba(0,0,0,0.7)",
                        color: "white",
                        p: "2px 8px",
                        borderRadius: 1,
                        fontSize: "0.8rem"
                    }}
                >
                    {currentIndex + 1} / {galleryItems.length}
                </Box>
                <IconButton 
                    onClick={handleOpenFullscreen}
                    sx={{ 
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "white",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.7)"
                        }
                    }}
                >
                    <Fullscreen />
                </IconButton>
            </Box>

            {galleryItems.length > 1 && (
                <Grid container spacing={1}>
                    {galleryItems.map((item, index) => (
                        <Grid item xs={3} sm={2} key={index}>
                            {renderThumbnail(item, index)}
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Fullscreen Modal */}
            <Modal
                open={fullscreenOpen}
                onClose={handleCloseFullscreen}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500
                }}
            >
                <Fade in={fullscreenOpen}>
                    <Box sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "90vw",
                        height: "90vh",
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        outline: "none"
                    }}>
                        <IconButton
                            onClick={handleCloseFullscreen}
                            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1, color: "text.primary" }}
                        >
                            <Close />
                        </IconButton>

                        <Box sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between"
                        }}>
                            <IconButton onClick={prevItem} sx={{ color: "primary.main" }}>
                                <ChevronLeft />
                            </IconButton>
                            <Box sx={{ width: "calc(100% - 100px)", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {renderMedia(galleryItems[currentIndex], { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" })}
                            </Box>
                            <IconButton onClick={nextItem} sx={{ color: "primary.main" }}>
                                <ChevronRight />
                            </IconButton>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </Paper>
    );
};

export default ImageGallery; 